<template>
    <div class="metrics-dashboard">
        <div class="dashboard-controls">
            <Select
                v-model="selectedHours"
                :options="timeRangeOptions"
                optionLabel="label"
                optionValue="value"
                class="time-range-selector"
            />
        </div>
        <div v-if="loading" class="loading">
            <Spinner />
        </div>
        <div v-else-if="error" class="error">
            <Message severity="error">{{ error }}</Message>
        </div>
        <div v-else-if="metrics" class="metrics">
            <div class="metric-cards">
                <Card>
                    <template #title>Process Instances</template>
                    <template #content>
                        <div class="metric-value">
                            {{ getMetricValue("process_instance_count") }}
                        </div>
                    </template>
                </Card>
                <Card>
                    <template #title>Total Events</template>
                    <template #content>
                        <div class="metric-value">
                            {{ getMetricValue("total_events") }}
                        </div>
                    </template>
                </Card>
                <Card>
                    <template #title>Error Count</template>
                    <template #content>
                        <div class="metric-value">
                            {{ getMetricValue("error_count") }}
                        </div>
                    </template>
                </Card>
                <Card>
                    <template #title>Error Rate</template>
                    <template #content>
                        <div class="metric-value">{{ getErrorRate() }}%</div>
                    </template>
                </Card>
            </div>

            <Card class="events-by-type">
                <template #title>Events by Type</template>
                <template #content>
                    <DataTable :value="eventsByTypeList" :rows="10">
                        <Column field="type" header="Event Type"></Column>
                        <Column field="count" header="Count"></Column>
                    </DataTable>
                </template>
            </Card>

            <Card class="time-series">
                <template #title>
                    <div class="time-series-header">
                        <span>Time Series</span>
                    </div>
                </template>
                <template #content>
                    <div class="chart-container">
                        <canvas ref="chartCanvas"></canvas>
                    </div>
                </template>
            </Card>
        </div>
        <slot
            v-if="!loading && !error"
            name="child-section"
            :hours="selectedHours"
            :stek-release="selectedRelease"
            :entity-id="entityId"
        />
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Message from "primevue/message";
import Select from "primevue/select";
import { toRef } from "vue";

import { useMetricsDashboard } from "@/composables/useMetricsDashboard";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "MetricsDashboard",
});

const props = defineProps<{
    entityId: string;
    entityType: string;
}>();

const {
    metrics,
    error,
    loading,
    chartCanvas,
    selectedHours,
    selectedRelease,
    eventsByTypeList,
    timeRangeOptions,
    getMetricValue,
    getErrorRate,
} = useMetricsDashboard({
    entityId: toRef(props, "entityId"),
    entityType: toRef(props, "entityType"),
});
</script>

<style scoped>
.metrics-dashboard {
    padding: 0;
}

.dashboard-controls {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-bottom: 1rem;
}

.metric-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
}

.metric-cards :deep(.p-card-title) {
    height: 3rem;
    display: flex;
    align-items: flex-end;
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
}

.metric-value {
    font-size: 2.5rem;
    font-weight: bold;
    color: var(--stekz-primary-color);
    line-height: 1;
}

.events-by-type,
.time-series {
    margin-top: 2rem;
}

.time-series-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.time-range-selector {
    width: 150px;
}

.chart-container {
    position: relative;
    height: 300px;
    width: 100%;
    min-height: 300px;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
